import React, { useState } from 'react';
import { Box, Button } from '@mui/material';

import { MaterialSelection, OrganizationPreset } from '@paragon/contract-library';

import { SettingsOfType, SettingsPresetType } from '../../../../dialogs/presets/presetTypes';
import { ApplyPresetDialog } from '../../../../dialogs/project-wide-settings/ApplyPresetDialog';
import { SavePresetDialog } from '../../../../dialogs/project-wide-settings/SavePresetDialog';
import { useFetchedPresetsOfSingleType } from '../../../../dialogs/project-wide-settings/useFetchedPresets';

interface Props<T extends SettingsPresetType> {
  settingsType: T;

  /**
   * Saving a new preset will be disabled if `settings` is `null`. This is expected when there is not a determinate
   *   settings object for the active context.
   */
  settings: SettingsOfType<T> | null;

  materialSelection?: MaterialSelection | null;

  onOverwriteSettingsFromPreset: (settings: SettingsOfType<T>, preset: OrganizationPreset<SettingsOfType<T>>) => void;
}

export function SettingsControlsPresetToolbar<T extends SettingsPresetType>(props: Props<T>) {
  const { presets, invalidate } = useFetchedPresetsOfSingleType(props.settingsType);

  const [showingSavePresetDialog, setShowingSavePresetDialog] = useState(false);
  const [showingApplyPresetDialog, setShowingApplyPresetDialog] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, marginX: 1, marginY: 2 }}>
        <Button
          variant="outlined"
          disabled={presets === 'fetching' || presets === 'error' || props.settings == null}
          onClick={() => {
            setShowingSavePresetDialog(true);
          }}
        >
          Save Current Settings As Preset
        </Button>

        {/* spacer */}
        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          onClick={() => {
            setShowingApplyPresetDialog(true);
          }}
        >
          Apply Preset...
        </Button>
      </Box>

      {showingSavePresetDialog && props.settings != null && (
        <SavePresetDialog
          settingsType={props.settingsType}
          settings={props.settings}
          materialSelection={props.materialSelection}
          onPresetsInvalidated={async () => invalidate()}
          onClose={() => setShowingSavePresetDialog(false)}
        />
      )}

      {showingApplyPresetDialog && typeof presets !== 'string' && (
        <ApplyPresetDialog
          presets={presets}
          onApplyPreset={(preset) => {
            props.onOverwriteSettingsFromPreset(preset.settings, preset);
            setShowingApplyPresetDialog(false);
          }}
          onClose={() => setShowingApplyPresetDialog(false)}
        />
      )}
    </>
  );
}
